import { useAtom, useAtomValue } from "jotai";
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { ChatService } from "../services/chatService";
import {
  anonymousMessagesAtom,
  chatListLoadingAtom,
  chatLoadingAtom,
  chatMessageAtom,
  chatsAtom,
  currentChatAtom,
  isStreamingAtom,
  streamingContentAtom,
} from "../store/atoms";
import { sortedChatsAtom } from "../store/selectors";
import { ChatWithMessages, Message } from "../types/chat";

/** 타이핑 애니메이션 속도 (ms/글자) */
const TYPING_DELAY_CHAR = 20;
const TYPING_DELAY_NEWLINE = 80;

export const useChat = () => {
  const [, setChats] = useAtom(chatsAtom);
  const [currentChat, setCurrentChat] = useAtom(currentChatAtom);
  const [message, setMessage] = useAtom(chatMessageAtom);
  const [isLoading, setIsLoading] = useAtom(chatLoadingAtom);
  const [isChatListLoading, setIsChatListLoading] =
    useAtom(chatListLoadingAtom);
  const [anonymousMessages, setAnonymousMessages] = useAtom(
    anonymousMessagesAtom
  );
  const [isStreaming, setIsStreaming] = useAtom(isStreamingAtom);
  const [streamingContent, setStreamingContent] = useAtom(
    streamingContentAtom
  );
  const sortedChats = useAtomValue(sortedChatsAtom);

  // 스트리밍 중단을 위한 AbortController 레퍼런스
  const abortControllerRef = useRef<AbortController | null>(null);
  // 화면에 표시된 글자 수 (타이핑 애니메이션 위치)
  const displayedLengthRef = useRef<number>(0);
  // Gemini에서 실제로 수신한 전체 텍스트 (DB 저장 내용과 동기화)
  const fullReceivedContentRef = useRef<string>("");
  // 타이핑 애니메이션 타이머
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // done 이벤트 도착 시 타이핑 애니메이션 완료 후 실행할 콜백
  const pendingDoneCallbackRef = useRef<(() => void) | null>(null);

  /**
   * 타이핑 애니메이션: fullReceivedContentRef에서 한 글자씩 streamingContent로 표시
   * UI 레이어에서 애니메이션을 처리 (서비스 레이어와 관심사 분리)
   */
  const scheduleNextChar = useCallback(() => {
    const full = fullReceivedContentRef.current;
    const pos = displayedLengthRef.current;

    if (pos >= full.length) {
      // 수신된 글자를 모두 표시함
      typingTimerRef.current = null;

      // done 이벤트가 이미 도착했으면 → 애니메이션 완료 후 finalize 실행
      if (pendingDoneCallbackRef.current) {
        const callback = pendingDoneCallbackRef.current;
        pendingDoneCallbackRef.current = null;
        callback();
      }
      return;
    }

    const char = full[pos];
    displayedLengthRef.current = pos + 1;
    setStreamingContent(full.slice(0, pos + 1));

    const delay = char === "\n" ? TYPING_DELAY_NEWLINE : TYPING_DELAY_CHAR;
    typingTimerRef.current = setTimeout(scheduleNextChar, delay);
  }, [setStreamingContent]);

  /** 새 청크 수신 시 타이핑 애니메이션 시작 (이미 돌고 있으면 무시) */
  const onChunkReceived = useCallback(
    (chunk: string) => {
      fullReceivedContentRef.current += chunk;
      // 타이머가 없으면 (대기 중이면) 즉시 시작
      if (!typingTimerRef.current) {
        scheduleNextChar();
      }
    },
    [scheduleNextChar]
  );

  /** 타이핑 애니메이션을 현재 표시된 위치에서 정지 (나머지 버림) */
  const freezeTypingAnimation = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    // 현재 화면에 표시된 부분까지만 유지
    const displayed = fullReceivedContentRef.current.slice(
      0,
      displayedLengthRef.current
    );
    fullReceivedContentRef.current = displayed;
    return displayed;
  }, []);

  /** 스트리밍 상태 초기화 */
  const resetStreamingState = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    pendingDoneCallbackRef.current = null;
    setIsStreaming(false);
    setStreamingContent("");
    displayedLengthRef.current = 0;
    fullReceivedContentRef.current = "";
  }, [setIsStreaming, setStreamingContent]);

  // 채팅 목록 조회
  const loadChatList = useCallback(async () => {
    try {
      setIsChatListLoading(true);
      const chatList = await ChatService.getChatList();
      setChats(chatList);
    } catch (error) {
      console.error("채팅 목록 로드 실패:", error);
      toast.error("채팅 목록을 불러오는데 실패했습니다.");
    } finally {
      setIsChatListLoading(false);
    }
  }, [setChats, setIsChatListLoading]);

  // 특정 채팅 조회
  const loadChat = useCallback(
    async (chatId: string) => {
      try {
        setIsLoading(true);
        const chat = await ChatService.getChat(chatId);
        setCurrentChat(chat);
      } catch (error) {
        console.error("채팅 로드 실패:", error);
        toast.error("채팅을 불러오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    },
    [setCurrentChat, setIsLoading]
  );

  // 새 채팅 시작 (SSE 스트리밍)
  const startNewChat = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;
      fullReceivedContentRef.current = "";
      displayedLengthRef.current = 0;
      pendingDoneCallbackRef.current = null;

      // 낙관적으로 임시 채팅 + 사용자 메시지 즉시 표시
      const tempChatId = `temp-chat-${Date.now()}`;
      const tempUserMsg: Message = {
        id: `temp-user-${Date.now()}`,
        chatId: tempChatId,
        role: "USER",
        content,
        createdAt: new Date().toISOString(),
      };
      const tempChat: ChatWithMessages = {
        id: tempChatId,
        title: "",
        userId: "",
        messages: [tempUserMsg],
        lastMessageAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
      setCurrentChat(tempChat);
      setIsStreaming(true);
      setStreamingContent("");

      try {
        await ChatService.startChatStream(
          { content },
          {
            onStart: (data) => {
              if (data.chat) {
                // 서버에서 실제 채팅 정보를 받으면 임시 채팅을 교체 (사용자 메시지 유지)
                // 제목은 비워두고, 백그라운드에서 생성된 실제 제목이 onTitle로 도착할 때 표시
                const chatWithoutTitle = { ...data.chat!, title: "" };
                setCurrentChat((prev) => {
                  const existingMessages = (prev?.messages || []).map((m) => ({
                    ...m,
                    chatId: data.chat!.id,
                  }));
                  return {
                    ...chatWithoutTitle,
                    messages: existingMessages,
                  };
                });
                setChats((prev) => [chatWithoutTitle, ...prev]);
              }
            },
            onToken: (chunk) => {
              onChunkReceived(chunk);
            },
            onTitle: (data) => {
              // 백그라운드에서 생성된 채팅 제목 업데이트
              setCurrentChat((prev) => {
                if (!prev || prev.id !== data.chatId) return prev;
                return { ...prev, title: data.title };
              });
              setChats((prev) =>
                prev.map((chat) =>
                  chat.id === data.chatId
                    ? { ...chat, title: data.title }
                    : chat
                )
              );
            },
            onDone: (data) => {
              const finalize = () => {
                if (data.userMessage && data.aiMessage) {
                  setCurrentChat((prev) => {
                    if (!prev) return prev;
                    const nonTempMessages = prev.messages.filter(
                      (m) => !m.id.startsWith("temp-")
                    );
                    return {
                      ...prev,
                      messages: [
                        ...nonTempMessages,
                        data.userMessage!,
                        data.aiMessage!,
                      ],
                      lastMessageAt: data.aiMessage!.createdAt,
                    };
                  });

                  setChats((prev) =>
                    prev.map((chat) =>
                      chat.id === data.aiMessage!.chatId
                        ? {
                            ...chat,
                            lastMessageAt: data.aiMessage!.createdAt,
                          }
                        : chat
                    )
                  );
                }
                resetStreamingState();
              };

              // 타이핑 애니메이션이 아직 진행 중이면 완료 후 finalize
              if (
                displayedLengthRef.current >=
                fullReceivedContentRef.current.length
              ) {
                finalize();
              } else {
                pendingDoneCallbackRef.current = finalize;
              }
            },
            onError: (errorMessage) => {
              toast.error(errorMessage || "채팅 시작에 실패했습니다.");
              resetStreamingState();
            },
          },
          controller.signal
        );
      } catch (error: unknown) {
        const isAbort =
          error instanceof DOMException && error.name === "AbortError";
        if (!isAbort) {
          toast.error("새 채팅을 시작하는데 실패했습니다.");
        }
        // 중단 시: 현재 화면에 표시된 부분까지만 유지
        if (isAbort) {
          const displayedContent = freezeTypingAnimation();
          if (displayedContent) {
            setCurrentChat((prev) => {
              if (!prev) return prev;
              const partialAiMsg: Message = {
                id: `partial-ai-${Date.now()}`,
                chatId: prev.id,
                role: "AI",
                content: displayedContent,
                createdAt: new Date().toISOString(),
              };
              return {
                ...prev,
                messages: [...prev.messages, partialAiMsg],
              };
            });
          }
        }
        resetStreamingState();
      }
    },
    [
      setChats,
      setCurrentChat,
      setIsStreaming,
      setStreamingContent,
      onChunkReceived,
      freezeTypingAnimation,
      resetStreamingState,
    ]
  );

  // 메시지 전송 (SSE 스트리밍)
  const sendMessage = useCallback(
    async (chatId: string, content: string) => {
      if (!content.trim()) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;
      fullReceivedContentRef.current = "";
      displayedLengthRef.current = 0;
      pendingDoneCallbackRef.current = null;

      // 낙관적으로 사용자 메시지 즉시 표시
      const tempUserMsg: Message = {
        id: `temp-user-${Date.now()}`,
        chatId,
        role: "USER",
        content,
        createdAt: new Date().toISOString(),
      };

      setCurrentChat((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [...prev.messages, tempUserMsg],
        };
      });

      setIsStreaming(true);
      setStreamingContent("");

      try {
        await ChatService.sendMessageStream(
          { chatId, content },
          {
            onToken: (chunk) => {
              onChunkReceived(chunk);
            },
            onDone: (data) => {
              const finalize = () => {
                if (data.userMessage && data.aiMessage) {
                  setCurrentChat((prev) => {
                    if (!prev) return prev;
                    const nonTempMessages = prev.messages.filter(
                      (m) => !m.id.startsWith("temp-")
                    );
                    return {
                      ...prev,
                      messages: [
                        ...nonTempMessages,
                        data.userMessage!,
                        data.aiMessage!,
                      ],
                      lastMessageAt: data.aiMessage!.createdAt,
                    };
                  });

                  setChats((prev) =>
                    prev.map((chat) =>
                      chat.id === chatId
                        ? {
                            ...chat,
                            lastMessageAt: data.aiMessage!.createdAt,
                          }
                        : chat
                    )
                  );
                }
                resetStreamingState();
              };

              if (
                displayedLengthRef.current >=
                fullReceivedContentRef.current.length
              ) {
                finalize();
              } else {
                pendingDoneCallbackRef.current = finalize;
              }
            },
            onError: (errorMessage) => {
              toast.error(errorMessage || "메시지 전송에 실패했습니다.");
              resetStreamingState();
            },
          },
          controller.signal
        );
      } catch (error: unknown) {
        const isAbort =
          error instanceof DOMException && error.name === "AbortError";
        if (!isAbort) {
          toast.error("메시지 전송에 실패했습니다.");
        }
        // 중단 시: 현재 화면에 표시된 부분까지만 유지
        if (isAbort) {
          const displayedContent = freezeTypingAnimation();
          if (displayedContent) {
            setCurrentChat((prev) => {
              if (!prev) return prev;
              const partialAiMsg: Message = {
                id: `partial-ai-${Date.now()}`,
                chatId,
                role: "AI",
                content: displayedContent,
                createdAt: new Date().toISOString(),
              };
              return {
                ...prev,
                messages: [...prev.messages, partialAiMsg],
              };
            });
          }
        }
        resetStreamingState();
      }
    },
    [
      setCurrentChat,
      setChats,
      setIsStreaming,
      setStreamingContent,
      onChunkReceived,
      freezeTypingAnimation,
      resetStreamingState,
    ]
  );

  // 스트리밍 중단
  const stopStreaming = useCallback(() => {
    // 1) fetch 스트림 중단 (아직 진행 중인 경우)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // 2) fetch가 이미 완료되었지만 타이핑 애니메이션만 남아있는 경우 처리
    //    (GCP LB 등 프록시가 SSE를 버퍼링하면 fetch는 즉시 끝나고 애니메이션만 돌고 있음)
    if (typingTimerRef.current || pendingDoneCallbackRef.current) {
      const displayedContent = freezeTypingAnimation();

      // pendingDoneCallbackRef가 있으면 onDone이 이미 도착한 상태
      // → finalize 대신 현재 표시된 내용으로 메시지를 직접 구성
      pendingDoneCallbackRef.current = null;

      if (displayedContent) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          // 이미 확정된 AI 메시지가 있으면 중복 추가 방지
          const lastMsg = prev.messages[prev.messages.length - 1];
          if (lastMsg?.role === "AI" && !lastMsg.id.startsWith("temp-") && !lastMsg.id.startsWith("partial-")) {
            return prev;
          }
          const partialAiMsg: Message = {
            id: `partial-ai-${Date.now()}`,
            chatId: prev.id,
            role: "AI",
            content: displayedContent,
            createdAt: new Date().toISOString(),
          };
          return {
            ...prev,
            messages: [...prev.messages, partialAiMsg],
          };
        });
      }

      resetStreamingState();
    }
  }, [freezeTypingAnimation, resetStreamingState, setCurrentChat]);

  // 채팅 삭제
  const deleteChat = useCallback(
    async (chatId: string) => {
      try {
        await ChatService.deleteChat(chatId);

        // 채팅 목록에서 제거
        setChats((prev) => prev.filter((chat) => chat.id !== chatId));

        // 현재 채팅이 삭제된 채팅이면 초기화
        setCurrentChat((prev) => (prev?.id === chatId ? null : prev));

        toast.success("채팅이 삭제되었습니다.");
      } catch (error) {
        console.error("채팅 삭제 실패:", error);
        toast.error("채팅 삭제에 실패했습니다.");
      }
    },
    [setChats, setCurrentChat]
  );

  // 새 채팅으로 전환
  const createNewChat = useCallback(() => {
    setCurrentChat(null);
    setMessage("");
  }, [setCurrentChat, setMessage]);

  // 익명 메시지 전송 (SSE 스트리밍)
  const sendAnonymousMessage = useCallback(
    async (content: string): Promise<void> => {
      if (!content.trim()) {
        throw new Error("메시지 내용이 비어있습니다.");
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      fullReceivedContentRef.current = "";
      displayedLengthRef.current = 0;
      pendingDoneCallbackRef.current = null;

      // 사용자 메시지를 익명 메시지 목록에 즉시 추가
      const userMessage = {
        id: Date.now().toString(),
        role: "USER" as const,
        content,
        timestamp: new Date().toISOString(),
      };
      setAnonymousMessages((prev) => [...prev, userMessage]);

      setIsStreaming(true);
      setStreamingContent("");

      try {
        await ChatService.sendAnonymousMessageStream(
          { content },
          {
            onToken: (chunk) => {
              onChunkReceived(chunk);
            },
            onDone: (data) => {
              const finalize = () => {
                const aiMessage = {
                  id: (Date.now() + 1).toString(),
                  role: "AI" as const,
                  content: data.response || fullReceivedContentRef.current,
                  timestamp: data.timestamp || new Date().toISOString(),
                };
                setAnonymousMessages((prev) => [...prev, aiMessage]);
                resetStreamingState();
              };

              if (
                displayedLengthRef.current >=
                fullReceivedContentRef.current.length
              ) {
                finalize();
              } else {
                pendingDoneCallbackRef.current = finalize;
              }
            },
            onError: (errorMessage) => {
              toast.error(errorMessage || "메시지 전송에 실패했습니다.");
              setAnonymousMessages((prev) => prev.slice(0, -1));
              resetStreamingState();
            },
          },
          controller.signal
        );
      } catch (error: unknown) {
        const isAbort =
          error instanceof DOMException && error.name === "AbortError";
        if (!isAbort) {
          toast.error("메시지 전송에 실패했습니다.");
          setAnonymousMessages((prev) => prev.slice(0, -1));
        }
        // 중단 시: 현재 화면에 표시된 부분까지만 유지
        if (isAbort) {
          const displayedContent = freezeTypingAnimation();
          if (displayedContent) {
            const partialAiMsg = {
              id: (Date.now() + 1).toString(),
              role: "AI" as const,
              content: displayedContent,
              timestamp: new Date().toISOString(),
            };
            setAnonymousMessages((prev) => [...prev, partialAiMsg]);
          }
        }
        resetStreamingState();
      }
    },
    [
      setIsStreaming,
      setStreamingContent,
      setAnonymousMessages,
      onChunkReceived,
      freezeTypingAnimation,
      resetStreamingState,
    ]
  );

  return {
    // 상태
    chats: sortedChats,
    currentChat,
    message,
    isLoading,
    isChatListLoading,
    anonymousMessages,
    isStreaming,
    streamingContent,

    // 액션
    setMessage,
    loadChatList,
    loadChat,
    startNewChat,
    sendMessage,
    deleteChat,
    createNewChat,
    sendAnonymousMessage,
    stopStreaming,
  };
};
